import * as EmailValidator from 'email-validator';
import DropIn from "braintree-web-drop-in-react";
import EnvironmentVariables from '../../shared/environmentVariables';
import ExternalLinks from '../../shared/externalLinks';
import GooglePlacesAutocomplete from 'react-google-places-autocomplete';
import PageContent from '../page/PageContent';
import ReCAPTCHA from "react-google-recaptcha";
import React, { FunctionComponent, useState, useRef, useMemo } from 'react';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Box, Button, TextField, Typography, CircularProgress, Checkbox, FormControl, Select, MenuItem, InputLabel, FormLabel, Alert, Table, TableBody, TableCell, TableRow } from '@mui/material';
import { currencyFormatter } from '../../shared/utils';
import { EcommerceEvent } from '../../shared/types';
import { getStyles } from './css/RegistrationFormStyles';
import { useNavigate, useLocation } from 'react-router-dom';

const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
});

interface Props {
    clientToken: string,
    bulkPricingRules: any,
    baseCreditPrice: number,
    countries: { countryCode: string, countryName: string }[]
}

const calculateBasePrice = (credits: number, baseCreditPrice: number) => {
    return credits * baseCreditPrice;
}

const getBulkDiscountAmount = (basePrice: number, bulkDiscount: any) => {
    return currencyFormatter.format(basePrice * (bulkDiscount.percentageDiscount / 100));
}

const getPromotionDiscountAmount = (basePrice: number, bulkDiscount: any, promotionDiscount: any) => {
    const bulkDiscountAmount = Boolean(bulkDiscount) ? basePrice * (bulkDiscount.percentageDiscount / 100) : 0;
    const totalPrice = basePrice - bulkDiscountAmount;
    return currencyFormatter.format(totalPrice * (promotionDiscount.percentageDiscount / 100));
}

const getBulkDiscount = (credits: number, bulkPricingRules: any) => {
    var discount = null;
    for (var rule of bulkPricingRules) {
        if (credits >= rule.quantity && (discount == null || discount.percentageDiscount < rule.percentageDiscount)) {
            discount = rule;
        }
    }
    return discount;
}

const RegistrationForm: FunctionComponent<Props> = ({ clientToken, bulkPricingRules, baseCreditPrice, countries }) => {
    const location = useLocation();
    const state = location.state;
    const query = new URLSearchParams(location.search);
    let initialMonths: string | null = null;
    let initialProperties: string | null = null;
    let initialCredits: string | null = null;
    if (state) {
        initialMonths = state.initialMonths;
        initialProperties = state.initialProperties;
    }
    else {
        const additionalCredits = query.get('additionalCredits');
        if (additionalCredits) {
            initialCredits = additionalCredits;
            initialMonths = Math.ceil(Number(initialCredits) / 30).toString();
            initialProperties = "1";
        }
    }

    let credits = 0;

    if (initialMonths && initialProperties) {
        credits = Number(initialMonths) * Number(initialProperties) * 30;
    }

    const navigate = useNavigate();
    const isDesktopOrLaptop = useMediaQuery('(min-width:1224px)');
    const styles = getStyles(isDesktopOrLaptop);
    const [email, setEmail] = useState<string>('');
    const [firstName, setFirstName] = useState<string>('');
    const [lastName, setLastName] = useState<string>('');
    const [company, setCompany] = useState<string>('');
    const [country, setCountry] = useState<string>('US');
    const [password, setPassword] = useState<string>('');
    const [isSubmitting, setSubmitting] = useState<boolean>(false);
    const [months, setMonths] = useState<number>(initialMonths ? Number(initialMonths) : 0);
    const [properties, setProperties] = useState<number>(initialProperties ? Number(initialProperties) : 0);
    const [basePrice, setBasePrice] = useState<number>(calculateBasePrice(credits, baseCreditPrice));
    const [bulkDiscount, setBulkDiscount] = useState<any>(getBulkDiscount(credits, bulkPricingRules));
    const [promotionDiscount, setPromotionDiscount] = useState<any>(null);
    const [dropInMargin, setDropInMargin] = useState<number>(0);
    const [recaptchaValue, setRecaptchaValue] = useState<string | null>();
    credits = useMemo(() => { return months && properties ? months * properties * 30 : 0; }, [months, properties]);

    const calculatePrice = (basePrice: number, bulkDiscount: any, promotionDiscount?: any) => {
        let totalPrice = basePrice;

        if (bulkDiscount) {
            const newDiscountAmount = basePrice * (bulkDiscount.percentageDiscount / 100);
            totalPrice = totalPrice - newDiscountAmount;
        }

        if (promotionDiscount) {
            const newDiscountAmount = totalPrice * (promotionDiscount.percentageDiscount / 100);
            totalPrice = totalPrice - newDiscountAmount;
        }

        return totalPrice;
    }

    const [price, setPrice] = useState<number>(calculatePrice(basePrice, bulkDiscount))
    const [submitEnabled, setSubmitEnabled] = useState<boolean>();
    const [paymentInstance, setPaymentInstance] = useState<any>();
    const [nonce, setNonce] = useState<any>();
    const [agreedToTerms, setAgreedToTerms] = useState<boolean>(false);
    const [termsError, setTermsError] = useState<boolean>(false);
    const [discountCode, setDiscountCode] = useState<string>("");
    const [discountCodeError, setDiscountCodeError] = useState<boolean>(false);
    const [accountExistsError, setAccountExistsError] = useState<boolean>(false);
    const [address, setAddress] = useState<any>(null);
    const [addressError, setAddressError] = useState<boolean>(false);
    const [signUpError, setSignUpError] = useState<string | null>(null);
    const [emailError, setEmailError] = useState<boolean>(false);
    const [passwordError, setPasswordError] = useState<boolean>(false);
    const [firstNameError, setFirstNameError] = useState<boolean>(false);
    const [lastNameError, setLastNameError] = useState<boolean>(false);
    const [recaptchaError, setRecaptchaError] = useState<boolean>(false);
    const [addressInputBorder, setAddressInputBorder] = useState<string>('1px inset rgba(0, 0, 0, 0.25)');
    const zohoObject = { widgetcode: "be0683af84337cd2b98234a8e336ab3545b9c26aab7929c1fcd46b7a042bdc1b354bae78457b14f0239c5db2c7f58e3a6970830d0bca98e608f2ec39ec1bc0e0", values: {}, ready: function () { } };


    const handleFormSubmit: React.MouseEventHandler<HTMLElement> = async e => {
        e.preventDefault();
        setSubmitting(true);
        window.scrollTo(0, 0);
        setSignUpError(null);
        setAccountExistsError(false);

        if (!recaptchaValue) {
            setRecaptchaError(true);
            setSubmitting(false);
            return;
        }
        else {
            setRecaptchaError(false);
        }

        if (months && months > 0) {
            if (!properties || properties < 1) {
                setSignUpError("When purchasing additional months, please specify the number of properties.");
                setSubmitting(false);
                return;
            } 
        }

        if (!firstName || !firstName.trim()) {
            setFirstNameError(true);
            setSubmitting(false);
            return;
        }
        else {
            setFirstNameError(false);
        }

        if (!lastName || !lastName.trim()) {
            setLastNameError(true);
            setSubmitting(false);
            return;
        }
        else {
            setLastNameError(false);
        }

        if (!password || password.length < 5) {
            setPasswordError(true);
            setSubmitting(false);
            return;
        }
        else {
            setPasswordError(false);
        }

        if (!agreedToTerms) {
            setTermsError(true);
            setSubmitting(false);
            return;
        }
        else {
            setTermsError(false);
        }

        if (!address) {
            setAddressError(true);
            setSubmitting(false);
            return;
        }
        else {
            setAddressError(false);
        }

        if (!EmailValidator.validate(email)) {
            setEmailError(true);
            setSubmitting(false);
            return;
        }
        else {
            setEmailError(false);
        }

        const response = await fetch('/api/registration', {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                email,
                password,
                firstName,
                lastName,
                company,
                country: country === "US" ? "" : country,
                address: address.value.description,
                additionalCredits: credits,
                price: price ? price.toFixed(2) : "0.00",
                recaptchaValue,
                nonce,
                promotionDiscountID: Boolean(promotionDiscount) ? promotionDiscount.id : null
            })
        });

        const data = await response.json();
        if (response.ok) {
            // Building the tracking payload must never block the redirect to the
            // confirmation page - sign-up has already succeeded by this point.
            let ecommerce: EcommerceEvent | undefined;
            try {
                ecommerce = credits > 0
                    ? {
                        currency: "USD",
                        transaction_id: data.transactionId,
                        value: price,
                        coupon: promotionDiscount ? discountCode : undefined,
                        items: [{ item_id: "1", item_name: "VCS Credits", price: price / credits, quantity: credits }],
                        credit_card_entered: true
                    }
                    : {
                        currency: "USD",
                        transaction_id: data.transactionId,
                        value: 0,
                        items: [{ item_id: "0", item_name: "VCS Credits", price: 0, quantity: 30 }],
                        credit_card_entered: true
                    };
            } catch (error) {
                console.error(error);
            }

            if (data.loginToken) {
                navigate('/subscription/signup-complete', { state: { token: data.loginToken, isSubscription: true, ecommerce } });
            } else {
                navigate('/subscription/signup-complete', { state: { isSubscription: true, ecommerce } });
            }
        }
        else {
            if (response.status === 422) {
                setAccountExistsError(true);
            }
            else {
                if (data && data.message) {
                    setSignUpError(data.message);
                }
                else {
                    setSignUpError('An error has occurred during sign up. Please try again later.');
                }
            }
            setSubmitting(false);
            return;
        }
    }

    const onCountryChanged = (e: any) => {
        setCountry(e.target.value)
        setAddress(null);
    }

    const onMonthsChanged = (e: any) => {
        const updatedMonths = Number(e.target.value);
        setMonths(updatedMonths);

        if (!properties) {
            return;
        }

        credits = updatedMonths * properties * 30;

        const updatedBasePrice = calculateBasePrice(credits, baseCreditPrice);
        setBasePrice(updatedBasePrice);

        const updatedBulkDiscount = getBulkDiscount(credits, bulkPricingRules);
        setBulkDiscount(updatedBulkDiscount);


        let newPrice;
        if (promotionDiscount) {
            newPrice = calculatePrice(updatedBasePrice, updatedBulkDiscount, promotionDiscount);
        } else {
            newPrice = calculatePrice(updatedBasePrice, updatedBulkDiscount);
        }
        setPrice(newPrice);
    }

    const onPropertiesChanged = (e: any) => {
        const updatedProperties = Number(e.target.value);
        setProperties(updatedProperties);

        if (!months) {
            return;
        }

        credits = updatedProperties * months * 30;

        const updatedBasePrice = calculateBasePrice(credits, baseCreditPrice);
        setBasePrice(updatedBasePrice);

        const updatedBulkDiscount = getBulkDiscount(credits, bulkPricingRules);
        setBulkDiscount(updatedBulkDiscount);


        let newPrice;
        if (promotionDiscount) {
            newPrice = calculatePrice(updatedBasePrice, updatedBulkDiscount, promotionDiscount);
        } else {
            newPrice = calculatePrice(updatedBasePrice, updatedBulkDiscount);
        }
        setPrice(newPrice);
    }

    const updatePayment = async () => {
        const response = await paymentInstance.requestPaymentMethod();
        setNonce(response.nonce);
        setDropInMargin(3);
    }

    const onPaymentMethodRequestable = (e: any) => {
        setSubmitEnabled(e.paymentMethodIsSelected);
    }

    const onNoPaymentMethodRequestable = () => {
        setSubmitEnabled(false);
    }

    const onSetAgreedToTerms = (e: React.ChangeEvent<HTMLInputElement>) => {
        setAgreedToTerms(e.target.checked);
    }

    const onDiscountCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setDiscountCode(e.target.value);
    }

    const onApplyDiscountClicked = async () => {
        if (!discountCode) {
            return;
        }
        const res = await fetch(`/api/discount-code?code=${discountCode}`);

        if (!res.ok) {
            setDiscountCodeError(true);
            return;
        }
        setDiscountCodeError(false);
        const data = await res.json();
        const selectedDiscount = data.discount;
        setPromotionDiscount(selectedDiscount);
        const price = calculatePrice(basePrice, bulkDiscount, selectedDiscount);
        setPrice(price);
    }

    const getLowestDiscountRule = (bulkPricingRules: any) => {
        let lowestRule;
        for (let i = 0; i < bulkPricingRules.length; i++) {
            if (!lowestRule) {
                lowestRule = bulkPricingRules[i];
            }
            else {
                if (lowestRule.quantity > bulkPricingRules[i].quantity) {
                    lowestRule = bulkPricingRules[i];
                }
            }
        }
        return lowestRule;
    }

    const lowestDiscountRule = getLowestDiscountRule(bulkPricingRules);

    return (
        <Box>
            {isSubmitting && <Box sx={{ backgroundColor: 'white', zIndex: 999 }} position='sticky' top={0} height='100vh'>
                <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                    <Box display="flex" justifyContent="center"><CircularProgress /></Box>
                    <Box display="flex" justifyContent="center" marginTop={1}><Typography>Processing registration. Please wait. Do not refresh your browser.</Typography></Box>
                </Box>
            </Box>}
            <PageContent color={'tertiary'}>
                <Box sx={styles.header}>
                    <Typography variant="h2" color="textPrimary" gutterBottom sx={styles.pageTitle}>Sign Up</Typography>
                </Box>
                {promotionDiscount && <Box display="flex" justifyContent="center"><Box m={2}><Alert severity="success">Discount applied.</Alert></Box></Box>}
                {discountCodeError && <Box display="flex" justifyContent="center"><Box m={2}><Alert severity="warning">The discount code was not recognized.</Alert></Box></Box>}
                {accountExistsError && <Box display="flex" justifyContent="center"><Box m={2}><Alert severity="warning">There is already an existing registered user with that email address. Click <a href={ExternalLinks.VCS_PORTAL.SUBSCRIPTION}>here</a> to log in to an existing account, where you can add credits.</Alert></Box></Box>}
                {addressError && <Box display="flex" justifyContent="center"><Box m={2}><Alert severity="warning">Please provide an address.</Alert></Box></Box>}
                {signUpError && <Box display="flex" justifyContent="center"><Box m={2}><Alert severity="warning">{signUpError}</Alert></Box></Box>}
                {emailError && <Box display="flex" justifyContent="center"><Box m={2}><Alert severity="warning">Please provide a valid email.</Alert></Box></Box>}
                {passwordError && <Box display="flex" justifyContent="center"><Box m={2}><Alert severity="warning">Please provide a password that's at least 5 characters long.</Alert></Box></Box>}
                {firstNameError && <Box display="flex" justifyContent="center"><Box m={2}><Alert severity="warning">Please provide a first name.</Alert></Box></Box>}
                {lastNameError && <Box display="flex" justifyContent="center"><Box m={2}><Alert severity="warning">Please provide a last name.</Alert></Box></Box>}
                {termsError && <Box display="flex" justifyContent="center"><Box m={2}><Alert severity="warning">Please accept the terms of use.</Alert></Box></Box>}
                {recaptchaError && <Box display="flex" justifyContent="center"><Box m={2}><Alert severity="warning">Please verify that you are not a robot.</Alert></Box></Box>}
                <hr />
                <Box sx={styles.contentBody}>
                    <Box sx={styles.form}>
                        <TextField sx={styles.formItem} id="email" label="Email" onChange={e => { setEmail(e.target.value) }} />
                        <TextField sx={styles.formItem} id="first-name" label="First Name" onChange={e => { setFirstName(e.target.value) }} />
                        <TextField sx={styles.formItem} id="last-name" label="Last Name" onChange={e => { setLastName(e.target.value) }} />
                        <TextField sx={styles.formItem} id="company" label="Company Name (optional)" onChange={e => { setCompany(e.target.value) }} />
                        <TextField sx={styles.formItem} inputProps={{ minLength: 5 }} id="password" label="Password" type="password" helperText="The password must be at least 5 characters" onChange={e => { setPassword(e.target.value) }} />
                        <FormControl>
                            <InputLabel>Country</InputLabel>
                            <Select
                                label="Country"
                                value={country}
                                placeholder='Country'
                                onChange={onCountryChanged}
                            >
                                {countries.map(c => <MenuItem key={c.countryCode} value={c.countryCode ? c.countryCode : "US"}>{c.countryName}</MenuItem>)}
                            </Select>
                        </FormControl>
                        <FormControl sx={styles.addressInput}>
                            {address && <FormLabel style={{ fontSize: "0.75rem" }}>Address</FormLabel>}
                            <Box sx={styles.addressAutocomplete}>
                                <fieldset
                                    style={{
                                        borderRadius: '4px',
                                        border: addressInputBorder,
                                        cursor: 'text'
                                    }}
                                    onMouseEnter={() => setAddressInputBorder('1px inset rgba(0, 0, 0, 0.87)')}
                                    onMouseLeave={() => setAddressInputBorder('1px inset rgba(0, 0, 0, 0.25)')}
                                >
                                    {address && <legend style={{ fontSize: "0.75rem", color: 'rgba(0, 0, 0, 0.55)' }}>Address * </legend>}
                                    <GooglePlacesAutocomplete apiKey={EnvironmentVariables.GOOGLE_API_KEY} selectProps={{
                                        placeholder: 'Address',
                                        styles: {
                                            input: (provided: any) => ({
                                                ...provided,
                                                font: 'inherit'
                                            }),
                                            control: (provided: any) => ({
                                                ...provided,
                                                backgroundColor: 'transparent',
                                                boxShadow: 0,
                                                border: 'none',
                                                "&:hover": {
                                                    border: "none",
                                                    cursor: "pointer",
                                                    '&:active': {
                                                        border: 'none'
                                                    }
                                                }
                                            }),
                                            menu: (provided: any) => ({
                                                ...provided,
                                                left: '-14px',
                                                width: 'calc(100% + 28px)'
                                            }),
                                            indicatorSeparator: _ => ({
                                                display: 'none'
                                            }),
                                            option: (provided: any) => ({
                                                ...provided,
                                                font: 'inherit',
                                            }),
                                            singleValue: (provided: any) => ({
                                                ...provided,
                                                font: 'inherit'
                                            }),
                                            dropdownIndicator: (provided: any) => ({
                                                ...provided,
                                                display: 'none'
                                            }),
                                            valueContainer: (provided: any) => ({
                                                ...provided,
                                                padding: 0
                                            })
                                        },
                                        value: address,
                                        onChange: setAddress,
                                        isDisabled: !Boolean(country)
                                    }}
                                        autocompletionRequest={{ componentRestrictions: { country: [country] }, types: ['address'] }}
                                    />
                                </fieldset>
                            </Box>
                        </FormControl>
                        <Box sx={{ my: 2 }}>
                            <ReCAPTCHA sitekey={EnvironmentVariables.RECAPTCHA_SITE_KEY as string} onChange={token => { setRecaptchaValue(token) }} />
                        </Box>
                    </Box>
                    <Box sx={styles.description}>
                        <Box display="flex" flexDirection={isDesktopOrLaptop ? "row" : "column"}>
                            <Box flex={6}>
                                <TextField sx={styles.creditsFormItem} id="months" label="Additional months" type="number" defaultValue={months} onChange={onMonthsChanged} inputProps={{ style: { textAlign: "center" } }} />
                            </Box>
                            <Box flex={6}>
                                <TextField sx={styles.creditsFormItem} id="properties" label="Number of properties" type="number" defaultValue={properties} onChange={onPropertiesChanged} inputProps={{ style: { textAlign: "center" } }} />
                            </Box>
                        </Box>
                        <Box>
                            <Box display="flex" flex={6}>
                                <TextField sx={styles.discountFormItem} id="discountCode" label="Discount Code" onChange={onDiscountCodeChange} />
                                <Box m={2}>
                                    <Button variant="contained" onClick={onApplyDiscountClicked}>Apply</Button>
                                </Box>
                            </Box>
                        </Box>
                        <Box padding={2} border={'1px solid #B5B5B5'} borderRadius={'4px'} marginTop={2} marginBottom={dropInMargin}>
                            <Typography><b>Order Summary</b></Typography>
                            <Table style={{ minWidth: isDesktopOrLaptop ? '300px' : 'auto' }}>
                                <TableBody>
                                    {(basePrice != price) && <TableRow>
                                        <TableCell sx={styles.tableCell}>{months === 1 ? '1 Month' : `${months} Months`} for {properties === 1 ? '1 Property' : `${properties} properties`}:</TableCell>
                                        <TableCell sx={styles.tableCell} align="right">{formatter.format(basePrice)}</TableCell>
                                    </TableRow>}
                                    {bulkDiscount && <TableRow>
                                        <TableCell sx={styles.tableCell}>Bulk Discount: <b>{bulkDiscount.percentageDiscount}% Off</b></TableCell>
                                        <TableCell sx={styles.tableCell} align="right">- {getBulkDiscountAmount(basePrice, bulkDiscount)}</TableCell>
                                    </TableRow>}
                                    {promotionDiscount && <TableRow>
                                        <TableCell sx={styles.tableCell}>Discount Code: <b>{promotionDiscount.percentageDiscount}% Off</b></TableCell>
                                        <TableCell sx={styles.tableCell} align="right">- {getPromotionDiscountAmount(basePrice, bulkDiscount, promotionDiscount)}</TableCell>
                                    </TableRow>}
                                    <TableRow>
                                        <TableCell sx={styles.tableCell}>Total Price:</TableCell>
                                        <TableCell sx={styles.tableCell} align="right">{formatter.format(price)}</TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </Box>
                        <DropIn
                            options={{ authorization: clientToken }}
                            onInstance={(instance) => {
                                setPaymentInstance(instance);
                            }}
                            onPaymentMethodRequestable={onPaymentMethodRequestable}
                            onNoPaymentMethodRequestable={onNoPaymentMethodRequestable}
                        />
                        <Box m={2} display="flex" justifyContent="flex-end">
                            <Button variant="contained" disabled={submitEnabled} onClick={updatePayment}>Update Payment</Button>
                        </Box>
                    </Box>
                    <Box display="flex" width="100%" justifyContent={'center'} marginTop={2} marginBottom={1}>
                        <Box width="80vw">
                            <Typography align="center">
                                If you do not add months of service above the free month that is included at no cost, your card will not be charged until you purchase additional service time.
                                Before your subscription term comes to an end, you will be notified. You can then purchase an extension of your choice, or simply allow auto-renewal to add a default of {Math.floor(lowestDiscountRule.quantity / 30)} {Math.floor(lowestDiscountRule.quantity / 30) === 1 ? 'month' : 'months'} at a {lowestDiscountRule.percentageDiscount}% discount.
                                Subscriptions are non-refundable.
                            </Typography>
                        </Box>
                    </Box>
                </Box>
                <FormControl sx={{ width: "100%" }} error={termsError}>
                    <Box display="flex" justifyContent={'center'} marginTop={1}>
                        <Checkbox onChange={onSetAgreedToTerms} /><Typography style={{ marginTop: "8px" }}>I agree with the <a target="_blank" rel="noopener" href={ExternalLinks.MARKETING.TERMS}>Terms of use</a></Typography>
                    </Box>
                </FormControl>
                <Box m={2} display="flex" justifyContent="center" marginBottom={10}>
                    <Button disabled={isSubmitting || !submitEnabled} color="secondary" variant="contained" onClick={handleFormSubmit}>Sign up</Button>
                </Box>
                <script type="text/javascript" id="zsiqchat">var $zoho=$zoho || { };$zoho.salesiq = $zoho.salesiq || zohoObject;var d=document;s=d.createElement("script");s.type="text/javascript";s.id="zsiqscript";s.defer=true;s.src="https://salesiq.zoho.com/widget";t=d.getElementsByTagName("script")[0];t.parentNode.insertBefore(s,t);</script>
            </PageContent>
        </Box>
    );
}

export default RegistrationForm;